import type { OrganizationTeam, Project, UserResource } from "@/engine/types";
import { EMPLOYMENT_LABEL, GRADE_LABEL } from "./resource-meta";
import type { ResourceWorkloadSummary } from "@/store/catalog-store";
import { formatDate } from "@/lib/date-format";
import { saveBytes } from "@/desktop/file-io";
import {
  ASSIGNMENT_HEADERS,
  EXAMPLE_FILL,
  HEADER_FILL,
  ORG_EXCEL_SCHEMA,
  ORG_SHEET,
  PEOPLE_HEADERS,
  READONLY_FILL,
  TAB_COLOR,
  TEAM_HEADERS,
  employmentChoices,
  formatActive,
  formatUtilization,
  gradeChoices,
  type ParsedPersonRow,
  type ParsedTeamRow,
} from "./org-excel-schema";
import type { OrgExcelParseInput, OrgIssue } from "./org-excel-preview";

type ExcelJSModule = typeof import("exceljs");
type Workbook = InstanceType<ExcelJSModule["Workbook"]>;
type Worksheet = ReturnType<Workbook["addWorksheet"]>;

const DATA_ROWS = 200;

function safeFilename(value: string): string {
  const cleaned = value.replace(/[\\/:*?"<>|]/g, "_").trim();
  return cleaned || "워크스페이스";
}

function cellText(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "Y" : "N";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object") {
    const rec = value as { text?: string; richText?: Array<{ text?: string }>; result?: unknown; hyperlink?: string };
    if (typeof rec.text === "string") return rec.text.trim();
    if (Array.isArray(rec.richText)) return rec.richText.map((t) => t.text ?? "").join("").trim();
    if (rec.result !== undefined) return cellText(rec.result);
    if (typeof rec.hyperlink === "string") return rec.hyperlink.trim();
  }
  return String(value).trim();
}

function paintHeader(sheet: Worksheet, count: number, fill = HEADER_FILL) {
  const header = sheet.getRow(1);
  header.height = 22;
  header.alignment = { vertical: "middle" };
  for (let c = 1; c <= count; c++) {
    const cell = header.getCell(c);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
    cell.font = { color: { argb: "FF424242" } };
    cell.border = {
      bottom: { style: "hair", color: { argb: "FFD9D9D9" } },
      right: { style: "hair", color: { argb: "FFE5E5E5" } },
    };
  }
}

function listValidation(formula: string, error: string) {
  return {
    type: "list" as const,
    allowBlank: true,
    formulae: [formula],
    showErrorMessage: true,
    errorTitle: "입력 값",
    error,
    showInputMessage: false,
  };
}

function addCodeSheet(wb: Workbook, name: string, rows: Array<[string, string]>, tabColor: string) {
  const sheet = wb.addWorksheet(name, { properties: { tabColor: { argb: tabColor } }, views: [{ state: "frozen", ySplit: 1 }] });
  sheet.columns = [
    { header: "코드", key: "code", width: 16 },
    { header: "라벨", key: "label", width: 18 },
  ];
  for (const [code, label] of rows) sheet.addRow([code, label]);
  paintHeader(sheet, 2);
  return sheet;
}

function addTeamSheet(wb: Workbook, name: string, rows: Array<Array<string | number | undefined>>, opts: { example?: boolean; tabColor: string }) {
  const sheet = wb.addWorksheet(name, { properties: { tabColor: { argb: opts.tabColor } }, views: [{ state: "frozen", ySplit: 1 }] });
  sheet.columns = [
    { header: TEAM_HEADERS[0], width: 18 },
    { header: TEAM_HEADERS[1], width: 24 },
    { header: TEAM_HEADERS[2], width: 36 },
    { header: TEAM_HEADERS[3], width: 16 },
    { header: TEAM_HEADERS[4], width: 12 },
  ];
  for (const row of rows) sheet.addRow(row);
  paintHeader(sheet, TEAM_HEADERS.length, opts.example ? EXAMPLE_FILL : HEADER_FILL);
  if (opts.example) {
    for (let r = 2; r <= sheet.rowCount; r++) {
      sheet.getRow(r).eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: EXAMPLE_FILL } };
      });
    }
  }
  return sheet;
}

function addPeopleSheet(
  wb: Workbook,
  name: string,
  rows: Array<Array<string | number | undefined>>,
  opts: { example?: boolean; tabColor: string; teamRange: string },
) {
  const sheet = wb.addWorksheet(name, { properties: { tabColor: { argb: opts.tabColor } }, views: [{ state: "frozen", ySplit: 1 }] });
  sheet.columns = [
    { header: PEOPLE_HEADERS[0], width: 18 },
    { header: PEOPLE_HEADERS[1], width: 14 },
    { header: PEOPLE_HEADERS[2], width: 28 },
    { header: PEOPLE_HEADERS[3], width: 22 },
    { header: PEOPLE_HEADERS[4], width: 20 },
    { header: PEOPLE_HEADERS[5], width: 14 },
    { header: PEOPLE_HEADERS[6], width: 14 },
    { header: PEOPLE_HEADERS[7], width: 14 },
    { header: PEOPLE_HEADERS[8], width: 28 },
    { header: PEOPLE_HEADERS[9], width: 24 },
    { header: PEOPLE_HEADERS[10], width: 8 },
    { header: PEOPLE_HEADERS[11], width: 10 },
  ];
  for (const row of rows) sheet.addRow(row);
  paintHeader(sheet, PEOPLE_HEADERS.length, opts.example ? EXAMPLE_FILL : HEADER_FILL);
  const last = Math.max(sheet.rowCount, DATA_ROWS);
  const empList = `"${employmentChoices().join(",")}"`;
  const gradeList = `"${gradeChoices().join(",")}"`;
  for (let r = 2; r <= last; r++) {
    sheet.getCell(r, 5).dataValidation = listValidation(opts.teamRange, "팀 시트에 있는 이름만 입력하세요");
    sheet.getCell(r, 6).dataValidation = listValidation(empList, "코드_고용형태 라벨만 입력하세요");
    sheet.getCell(r, 7).dataValidation = listValidation(gradeList, "코드_등급 라벨만 입력하세요");
    sheet.getCell(r, 11).dataValidation = listValidation('"Y,N"', "Y 또는 N");
    const util = sheet.getCell(r, 12);
    util.fill = { type: "pattern", pattern: "solid", fgColor: { argb: READONLY_FILL } };
    util.alignment = { horizontal: "right" };
    sheet.getCell(r, 8).alignment = { horizontal: "right" };
    sheet.getCell(r, 8).numFmt = "#,##0";
  }
  if (opts.example) {
    for (let r = 2; r <= sheet.rowCount; r++) {
      sheet.getRow(r).eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: EXAMPLE_FILL } };
      });
    }
  }
  return sheet;
}

function exampleTeamRows(): Array<Array<string>> {
  return [
    ["", "예시 플랫폼팀", "공통 프레임워크와 인프라 (예시, 적용되지 않음)", "홍길동", "#397dd5"],
    ["", "예시 협력단", "외주·프리랜서 풀 (예시, 적용되지 않음)", "김외주", "#5b5b5b"],
  ];
}

function examplePeopleRows(): Array<Array<string | number>> {
  return [
    ["", "홍길동", "hong@example.com", "시니어 백엔드", "예시 플랫폼팀", "정규직", "고급기술자", 9000000, "Java, Spring", "예시 행입니다", "Y", ""],
    ["", "김외주", "kim@partner.example", "프론트엔드", "예시 협력단", "외주협력사", "중급기술자", 7000000, "React", "예시 행입니다", "Y", ""],
  ];
}

function downloadBuffer(buffer: ArrayBuffer | Uint8Array | Blob, filename: string) {
  return saveBytes(filename, buffer, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
}

export async function exportOrgExcel(opts: {
  mode: "current" | "template";
  workspaceName: string;
  teams: OrganizationTeam[];
  resources: UserResource[];
  projects: Record<string, Project>;
  workloads: Record<string, ResourceWorkloadSummary>;
}): Promise<void> {
  const ExcelJS = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  wb.creator = "WIT Tracker";
  wb.created = new Date();

  const guide = wb.addWorksheet(ORG_SHEET.guide, { properties: { tabColor: { argb: TAB_COLOR.guide } } });
  guide.columns = [
    { width: 16 },
    { width: 72 },
  ];
  const exportedAt = formatDate(new Date(), "numericDateTime");
  const kind = opts.mode === "template" ? "빈 양식" : "현재 데이터";
  const meta: Array<[string, string]> = [
    ["스키마버전", ORG_EXCEL_SCHEMA],
    ["워크스페이스", opts.workspaceName],
    ["내보낸시각", exportedAt],
    ["종류", kind],
    ["", ""],
    ["규칙", "파란 탭(팀, 인력)만 업로드 시 적용됩니다."],
    ["", "초록 예시 탭, 노란 코드 탭, 회색 참고_투입은 적용하지 않습니다."],
    ["", "id가 있으면 해당 항목을 수정하고, 비어 있으면 신규로 추가합니다."],
    ["", "파일에 없는 기존 항목은 삭제되지 않습니다."],
    ["", "가동률은 읽기 전용입니다. 값이 있어도 무시합니다."],
    ["", "팀 열에는 팀 시트에 있는 이름을 그대로 입력하세요."],
    ["", "고용형태와 등급은 코드 탭의 라벨을 그대로 쓰세요."],
    ["", "없는 플랜·없는 팀은 적용되지 않고 미리보기에 오류로 표시됩니다."],
  ];
  for (const [k, v] of meta) guide.addRow([k, v]);
  guide.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };

  addTeamSheet(wb, ORG_SHEET.exampleTeam, exampleTeamRows(), { example: true, tabColor: TAB_COLOR.example });
  addPeopleSheet(wb, ORG_SHEET.examplePeople, examplePeopleRows(), {
    example: true,
    tabColor: TAB_COLOR.example,
    teamRange: `'${ORG_SHEET.exampleTeam}'!$B$2:$B$${DATA_ROWS}`,
  });
  addCodeSheet(wb, ORG_SHEET.codeEmployment, (Object.entries(EMPLOYMENT_LABEL) as Array<[string, string]>), TAB_COLOR.code);
  addCodeSheet(wb, ORG_SHEET.codeGrade, (Object.entries(GRADE_LABEL) as Array<[string, string]>), TAB_COLOR.code);

  const teamById = new Map(opts.teams.map((t) => [t.id, t]));
  const resourceById = new Map(opts.resources.map((r) => [r.id, r]));
  const teamRows =
    opts.mode === "template"
      ? []
      : opts.teams
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name, "ko"))
          .map((t) => {
            const leader = t.leaderId ? resourceById.get(t.leaderId) : undefined;
            return [t.id, t.name, t.description ?? "", leader?.displayName ?? "", t.color ?? ""];
          });
  addTeamSheet(wb, ORG_SHEET.teams, teamRows, { tabColor: TAB_COLOR.data });

  const peopleRows =
    opts.mode === "template"
      ? []
      : opts.resources
          .slice()
          .sort((a, b) => a.displayName.localeCompare(b.displayName, "ko"))
          .map((r) => {
            const team = r.teamId ? teamById.get(r.teamId) : undefined;
            const wl = opts.workloads[r.id];
            return [
              r.id,
              r.displayName,
              r.email,
              r.roleTitle,
              team?.name ?? "",
              EMPLOYMENT_LABEL[r.employmentType],
              GRADE_LABEL[r.grade],
              r.defaultMonthlyRate,
              r.skills.join(", "),
              r.notes ?? "",
              formatActive(r.isActive !== false),
              wl ? formatUtilization(wl.totalAllocationRatio) : "",
            ];
          });
  addPeopleSheet(wb, ORG_SHEET.people, peopleRows, {
    tabColor: TAB_COLOR.data,
    teamRange: `'${ORG_SHEET.teams}'!$B$2:$B$${DATA_ROWS}`,
  });

  const assignSheet = wb.addWorksheet(ORG_SHEET.assignments, {
    properties: { tabColor: { argb: TAB_COLOR.reference } },
    views: [{ state: "frozen", ySplit: 1 }],
  });
  assignSheet.columns = ASSIGNMENT_HEADERS.map((h, i) => ({ header: h, width: [16, 22, 14, 26, 18, 12, 12, 10, 8][i] }));
  paintHeader(assignSheet, ASSIGNMENT_HEADERS.length, READONLY_FILL);
  if (opts.mode === "current") {
    for (const project of Object.values(opts.projects)) {
      for (const asg of project.resourceAssignments ?? []) {
        const person = resourceById.get(asg.resourceId);
        assignSheet.addRow([
          project.id,
          project.name,
          person?.displayName ?? "",
          person?.email ?? "",
          asg.projectRole,
          asg.startDate,
          asg.endDate,
          Math.round(asg.allocationRatio * 100),
          asg.plannedMM,
        ]);
      }
    }
  }
  for (let r = 2; r <= Math.max(assignSheet.rowCount, 2); r++) {
    assignSheet.getRow(r).eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: READONLY_FILL } };
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  const suffix = opts.mode === "template" ? "조직인력_양식" : "조직인력";
  await downloadBuffer(buffer as ArrayBuffer, `${safeFilename(opts.workspaceName)}_${suffix}.xlsx`);
}

function headerIndex(sheet: Worksheet | undefined): Map<string, number> {
  const map = new Map<string, number>();
  if (!sheet) return map;
  const row = sheet.getRow(1);
  row.eachCell({ includeEmpty: false }, (cell, col) => {
    const key = cellText(cell.value);
    if (key) map.set(key, col);
  });
  return map;
}

function readMapped(sheet: Worksheet, headers: readonly string[]): { rows: string[][]; missing: string[] } {
  const index = headerIndex(sheet);
  const missing = headers.filter((h) => !index.has(h));
  const rows: string[][] = [];
  if (missing.length) return { rows, missing };
  for (let r = 2; r <= sheet.rowCount; r++) {
    const values = headers.map((h) => cellText(sheet.getRow(r).getCell(index.get(h)!).value));
    if (values.every((v) => !v)) continue;
    rows.push([String(r), ...values]);
  }
  return { rows, missing };
}

function issue(partial: Omit<OrgIssue, "action"> & { action?: string }): OrgIssue {
  return { action: "파일을 다시 확인", ...partial };
}

export async function readOrgExcelFile(file: File): Promise<OrgExcelParseInput> {
  const ExcelJS = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await wb.xlsx.load(buffer);
  return readOrgWorkbook(wb, file.name);
}

export function readOrgWorkbook(wb: Workbook, fileName: string): OrgExcelParseInput {
  const extraIssues: OrgIssue[] = [];
  const guide = wb.getWorksheet(ORG_SHEET.guide);
  let schemaVersion: string | null = null;
  if (guide) {
    for (let r = 1; r <= Math.min(guide.rowCount, 20); r++) {
      const key = cellText(guide.getRow(r).getCell(1).value);
      const value = cellText(guide.getRow(r).getCell(2).value);
      if (key === "스키마버전") schemaVersion = value || null;
    }
  }

  const teamSheet = wb.getWorksheet(ORG_SHEET.teams);
  const peopleSheet = wb.getWorksheet(ORG_SHEET.people);
  const assignSheet = wb.getWorksheet(ORG_SHEET.assignments);

  const teamsRead = teamSheet ? readMapped(teamSheet, TEAM_HEADERS) : { rows: [], missing: [...TEAM_HEADERS] };
  const peopleRead = peopleSheet ? readMapped(peopleSheet, PEOPLE_HEADERS) : { rows: [], missing: [...PEOPLE_HEADERS] };

  if (teamSheet && teamsRead.missing.length) {
    extraIssues.push(issue({ severity: "error", sheet: ORG_SHEET.teams, excelRow: 1, field: "헤더", value: teamsRead.missing.join(", "), message: `팀 탭에 필요한 열이 없습니다: ${teamsRead.missing.join(", ")}` }));
  }
  if (peopleSheet && peopleRead.missing.length) {
    extraIssues.push(issue({ severity: "error", sheet: ORG_SHEET.people, excelRow: 1, field: "헤더", value: peopleRead.missing.join(", "), message: `인력 탭에 필요한 열이 없습니다: ${peopleRead.missing.join(", ")}` }));
  }

  const teams: ParsedTeamRow[] = teamsRead.missing.length
    ? []
    : teamsRead.rows.map((cols) => ({
        excelRow: Number(cols[0]),
        id: cols[1] ?? "",
        name: cols[2] ?? "",
        description: cols[3] ?? "",
        leader: cols[4] ?? "",
        color: cols[5] ?? "",
      }));

  const people: ParsedPersonRow[] = peopleRead.missing.length
    ? []
    : peopleRead.rows.map((cols) => ({
        excelRow: Number(cols[0]),
        id: cols[1] ?? "",
        name: cols[2] ?? "",
        email: cols[3] ?? "",
        roleTitle: cols[4] ?? "",
        team: cols[5] ?? "",
        employment: cols[6] ?? "",
        grade: cols[7] ?? "",
        monthlyRate: cols[8] ?? "",
        skills: cols[9] ?? "",
        notes: cols[10] ?? "",
        active: cols[11] ?? "",
        utilization: cols[12] ?? "",
      }));

  let assignmentRowCount = 0;
  if (assignSheet && assignSheet.rowCount > 1) {
    for (let r = 2; r <= assignSheet.rowCount; r++) {
      const filled = ASSIGNMENT_HEADERS.some((_, i) => cellText(assignSheet.getRow(r).getCell(i + 1).value));
      if (filled) assignmentRowCount += 1;
    }
  }

  return {
    fileName,
    schemaVersion,
    hasGuide: Boolean(guide),
    hasTeams: Boolean(teamSheet),
    hasPeople: Boolean(peopleSheet),
    assignmentRowCount,
    teams,
    people,
    extraIssues,
  };
}
